import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { API } from '@/api'
import { ServerDomainInfo, Volume } from '@/api/type-server'
import { Button, message, Modal, Trans } from '@/components'
import { VolumeType } from '@/consts/machine'
import DriveVolumeQuotaSection from '@/pages/space-mgr/components/drive-volume-quota-section.tsx'
import ImageVolumeQuotaSection from '@/pages/space-mgr/components/image-volume-quota-section.tsx'
import { useUserStore } from '@/stores/user'
import { formatGB } from '@/utils/storage'
import { cn } from '@/utils/style'

import { EraseDataModal } from './erase-data-modal'
import VolumeQuotaSection from './volume-quota-section'
import { VolumeQuotaSegmentedBar } from './volume-quota-segmented-bar'

export interface SelectedData {
  selectedData: Array<SelectedDataItem>
  lastestVolume?: Volume
}

interface SelectedDataItem {
  volume: Volume
  selectedQuotaInGB: number
}

export default function VolumeQuotaModal(props: {
  open: boolean
  data: {
    snapshotVolumes?: Volume[]
    cloudVolumes?: Volume[]
    imageVolumes?: Volume[]
    domainInfos?: ServerDomainInfo[]
  }
  imageOnSpaceBilling?: boolean
  snapshotOnSpaceBilling?: boolean
  onClose: () => void
  onOk: () => void
}) {
  const {
    open,
    data,
    onClose,
    onOk,
    imageOnSpaceBilling = true,
    snapshotOnSpaceBilling = true,
  } = props
  const { cloudVolumes, imageVolumes, domainInfos } = data || {}
  const snapshotVolumes = useMemo(
    () => (snapshotOnSpaceBilling ? data?.snapshotVolumes : []),
    [snapshotOnSpaceBilling, data?.snapshotVolumes],
  )
  const userStore = useUserStore()
  const isOrderExpired = useUserStore((state) => state.isOrderExpired)

  const getInitialQuota = (vol: Volume) =>
    isOrderExpired ? vol.last_quota_gb || 0 : vol.quota_gb || 0

  const [loading, setLoading] = useState(false)
  const [eraseDataModalOpen, setEraseDataModalOpen] = useState(false)

  const partVolumeQuotaInGB = isOrderExpired
    ? userStore.partVolume?.last_quota_gb || 0
    : userStore.partVolume?.quota_gb || 0

  const [summarizedData, setSummarizedData] = useState({
    selectedData: [...(snapshotVolumes || []), ...(cloudVolumes || [])].map((val) => {
      return {
        volume: val,
        selectedQuotaInGB: getInitialQuota(val),
      } as SelectedDataItem
    }),
  } as SelectedData)

  const { availableInGB, imageVolumeQuota, hasModified, shouldDisableUpdate } = useMemo(() => {
    const { selectedData = [] } = summarizedData
    let selectedInGB = 0
    let hasInsufficientQuota = false
    let hasModified = false

    for (const item of selectedData) {
      selectedInGB += item.selectedQuotaInGB
      const { volume, selectedQuotaInGB } = item
      const rawUsageBytes = volume.usage_bytes ?? (volume.usage_mb || 0) * 1024 * 1024
      const usedGB = Number(((volume.usage_mb || 0) / 1024).toFixed(2))

      if (!hasModified && getInitialQuota(volume) !== selectedQuotaInGB) {
        hasModified = true
      }
      if (rawUsageBytes > 0 && selectedQuotaInGB < Math.max(usedGB, 1)) {
        hasInsufficientQuota = true
      }
    }

    const imageVolumeUsage = imageOnSpaceBilling
      ? (imageVolumes || []).reduce((sum, vol) => sum + vol.usage_mb / 1024, 0)
      : 0
    const imageVolumeMinQuota = Math.ceil(imageVolumeUsage) // Round up to nearest GB

    // Check for quota conflicts with Image Volume minimum requirement
    const hasQuotaConflict = selectedInGB + imageVolumeMinQuota > partVolumeQuotaInGB

    // Button should be disabled if there's insufficient quota OR quota conflict
    const shouldDisableUpdate = hasInsufficientQuota || hasQuotaConflict

    // Calculate adjusted quotas considering Image Volume minimum requirement
    const totalAllocatable = Math.max(0, partVolumeQuotaInGB - imageVolumeMinQuota)
    const availableInGB = Math.max(0, totalAllocatable - selectedInGB)
    const imageVolumeQuota = imageVolumeMinQuota + availableInGB

    return {
      availableInGB,
      imageVolumeQuota,
      hasModified,
      shouldDisableUpdate,
    }
  }, [summarizedData, partVolumeQuotaInGB, imageVolumes, imageOnSpaceBilling, isOrderExpired])

  const onVolumeChange = (volume: Volume, value: number) => {
    setSummarizedData((prev) => {
      const exists = prev.selectedData.some((item) => item.volume.id === volume.id)
      const newData = exists
        ? prev.selectedData.map((item) =>
            item.volume.id === volume.id ? { ...item, selectedQuotaInGB: value } : item,
          )
        : [...prev.selectedData, { volume, selectedQuotaInGB: value }]
      return { selectedData: newData, lastestVolume: volume }
    })
  }

  const onConfirm = async () => {
    // TODO: check if the new quota is less than the used space
    // const { selectedData = [] } = summarizedData
    // const modifiedVolumes = selectedData.filter((d) => d.volume.quota_gb !== d.selectedQuotaInGB)

    // const hasZeroQuotaDrive = modifiedVolumes.some(
    //   (d) => d.selectedQuotaInGB === 0 && d.volume.type === VolumeType.DataDrive,
    // )

    // const hasZeroQuotaSnapshot = modifiedVolumes.some(
    //   (d) => d.selectedQuotaInGB === 0 && d.volume.type === VolumeType.Snapshot,
    // )

    // if (hasZeroQuotaDrive || hasZeroQuotaSnapshot) {
    //   setEraseDataModalOpen(true)
    //   return
    // }

    await handleEraseDataConfirm()
  }

  const handleEraseDataConfirm = async () => {
    setEraseDataModalOpen(false)
    const { selectedData = [] } = summarizedData
    const modifiedVolumes = selectedData.filter(
      (d) => getInitialQuota(d.volume) !== d.selectedQuotaInGB,
    )

    // Classify operations: decrease quota first, then increase quota
    const decreaseOps = modifiedVolumes.filter(
      (i) => i.selectedQuotaInGB < getInitialQuota(i.volume),
    )
    const increaseOps = modifiedVolumes.filter(
      (i) => i.selectedQuotaInGB >= getInitialQuota(i.volume),
    )

    const createRequest = (item: SelectedDataItem) => {
      if (item.volume.id < 0) {
        return API.createSubvolume({
          type: 1,
          domain: item.volume.domain || 0,
          init_quota_gb: item.selectedQuotaInGB,
        })
          .then((response) => {
            return { success: !response || response.code === 0, response }
          })
          .catch((error) => ({ success: false, error }))
      } else {
        return API.modifySubvolume({
          id: item.volume.id,
          quota_gb: item.selectedQuotaInGB,
        })
          .then((response) => {
            return { success: !response || response.code === 0, response }
          })
          .catch((error) => ({ success: false, error }))
      }
    }

    try {
      setLoading(true)
      let allResults: Array<{ success: boolean; response?: any; error?: any }> = []

      // Phase 1: Execute all decrease operations first
      if (decreaseOps.length > 0) {
        const decreaseRequests = decreaseOps.map(createRequest)
        const decreaseResults = await Promise.all(decreaseRequests)
        allResults = [...decreaseResults]

        const decreaseSuccessCount = decreaseResults.filter((result) => result.success).length

        // Only proceed to Phase 2 if all decrease operations succeeded
        if (decreaseSuccessCount !== decreaseOps.length) {
          const totalCount = decreaseOps.length
          if (decreaseSuccessCount > 0) {
            message.show(
              `Partially successful: ${decreaseSuccessCount}/${totalCount} operations succeeded`,
            )
          } else {
            message.error('Modification failed')
          }
          onOk()
          return
        }
      }

      // Phase 2: Execute all increase operations
      if (increaseOps.length > 0) {
        const increaseRequests = increaseOps.map(createRequest)
        const increaseResults = await Promise.all(increaseRequests)
        allResults = [...allResults, ...increaseResults]
      }

      // Report combined results
      const successCount = allResults.filter((result) => result.success).length
      const totalCount = allResults.length

      if (successCount === totalCount) {
        message.success('Modification successful')
      } else if (successCount > 0) {
        message.show(`Partially successful: ${successCount}/${totalCount} operations succeeded`)
      } else {
        message.error('Modification failed')
      }

      onOk()
    } catch (error) {
      console.error('Error during volume modifications:', error)
      message.error('Modification failed')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      userStore.getOrder()
    }
  }, [open])

  // Sync props to state when modal opens or volumes data changes
  useEffect(() => {
    if (open) {
      setSummarizedData({
        selectedData: [...(snapshotVolumes || []), ...(cloudVolumes || [])].map((val) => ({
          volume: val,
          selectedQuotaInGB: getInitialQuota(val),
        })),
      })
    }
  }, [open, snapshotVolumes, cloudVolumes, isOrderExpired])

  return (
    <>
      <Modal
        className="w-[800px]"
        open={open}
        onClose={() => {
          if (!loading) {
            onClose()
          }
        }}
      >
        <Modal.Header>
          <Trans i18nKey="create.volumeQuotaModalTitle">Modify Quota</Trans>
        </Modal.Header>
        <Modal.Body className="overflow-y-auto max-h-[60vh]">
          <div className="t-bold text-primary">
            <Trans i18nKey="space.titleSpaceStorage">Storage Space</Trans>
          </div>

          <div className="my-4">
            <VolumeQuotaSegmentedBar
              summarizedData={summarizedData}
              imageOnSpaceBilling={imageOnSpaceBilling}
              snapshotOnSpaceBilling={snapshotOnSpaceBilling}
              domainInfos={domainInfos}
            />
          </div>

          <table className="table">
            <tbody>
              <tr className="h-[44px]">
                <td className="w-[120px] text-secondary t-sm !px-0 !py-3" align="left">
                  <Trans i18nKey="space.volumeQuotaModel.storage">Storage</Trans>
                </td>
                <td
                  className={cn(
                    ' t-sm !px-0 !py-3',
                    partVolumeQuotaInGB ? 'text-primary' : 'text-tertiary',
                  )}
                >
                  {partVolumeQuotaInGB ? formatGB(partVolumeQuotaInGB, { decimals: 0 }) : '-'}
                </td>
              </tr>
              <tr className="h-[44px]">
                <td className="w-[120px] text-secondary t-sm !px-0 !py-3" align="left">
                  <Trans i18nKey="space.volumeQuotaModel.expireOn">Expire</Trans>
                </td>
                <td
                  className={cn(
                    't-sm !px-0 !py-3',
                    userStore.order ? 'text-primary' : 'text-tertiary',
                  )}
                >
                  {userStore.order
                    ? dayjs(userStore.order.deadlineTime.seconds * 1000).format('YYYY-MM-DD')
                    : '-'}
                </td>
              </tr>
            </tbody>
          </table>

          <div className="mt-4 mb-2 flex flex-col gap-4 max-h-[300px] overflow-y-auto">
            {imageOnSpaceBilling && imageVolumes && (
              <ImageVolumeQuotaSection imageVolumeQuota={imageVolumeQuota} />
            )}
            {snapshotOnSpaceBilling && snapshotVolumes && (
              <VolumeQuotaSection
                isSnapshot={true}
                volumes={snapshotVolumes}
                availableInGB={availableInGB}
                summarizedData={summarizedData}
                onChange={onVolumeChange}
                isOrderExpired={isOrderExpired}
              />
            )}
            {cloudVolumes && (
              <DriveVolumeQuotaSection
                volumes={cloudVolumes}
                availableInGB={availableInGB}
                summarizedData={summarizedData}
                domainInfos={domainInfos}
                onChange={onVolumeChange}
                isOrderExpired={isOrderExpired}
                useGradientChips={!imageOnSpaceBilling && !snapshotOnSpaceBilling}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="outline"
            onClick={() => {
              if (!loading) {
                onClose()
              }
            }}
          >
            <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
          </Button>
          <Button
            type="secondary"
            disabled={loading || !hasModified || shouldDisableUpdate}
            onClick={onConfirm}
          >
            <Trans i18nKey="common.confirmModalUpdate">Update</Trans>
          </Button>
        </Modal.Footer>
      </Modal>
      <EraseDataModal
        open={eraseDataModalOpen}
        onClose={() => setEraseDataModalOpen(false)}
        onConfirm={handleEraseDataConfirm}
        domainNames={
          domainInfos
            ?.filter((domainInfo) => {
              const volume = [...(snapshotVolumes || []), ...(cloudVolumes || [])].find(
                (v) => v.domain === domainInfo.domain,
              )
              const selectedData = summarizedData.selectedData.find(
                (d) => d.volume.domain === domainInfo.domain,
              )
              return (
                selectedData?.selectedQuotaInGB === 0 && volume && getInitialQuota(volume) !== 0
              )
            })
            .map((d) => d.displayName) || []
        }
        dataDriveConfig={(() => {
          const modifiedVolumes = summarizedData.selectedData.filter(
            (d) => getInitialQuota(d.volume) !== d.selectedQuotaInGB,
          )
          return modifiedVolumes.some(
            (d) => d.selectedQuotaInGB === 0 && d.volume.type === VolumeType.DataDrive,
          )
            ? 0
            : 1
        })()}
        snapshotConfig={(() => {
          const modifiedVolumes = summarizedData.selectedData.filter(
            (d) => getInitialQuota(d.volume) !== d.selectedQuotaInGB,
          )
          return modifiedVolumes.some(
            (d) => d.selectedQuotaInGB === 0 && d.volume.type === VolumeType.Snapshot,
          )
            ? 0
            : 1
        })()}
      />
    </>
  )
}
